// SAM-590 — front-desk "Add-ons" block, rendered after ServiceQuantitySelector in
// ServiceBookingSelector.tsx. Qty steppers only (0..max_qty; a required add-on starts at 1 and
// can't go to 0) — same "never send/compute a price client-side" contract as
// ServiceQuantitySelector: the unit price shown is read verbatim off the add-on, never
// multiplied into a total. The server confirms the total (see BookingSummaryRail's note).

import { Minus, Plus } from 'lucide-react';
import type { ServiceAddOn } from '../../../hooks/useServices';
import { clampAddOnQuantity } from './serviceBooking';
import { labelOf } from './bookingService';
import { useT } from '../../../i18n/admin';

type Props = {
  addOns: ServiceAddOn[];
  quantities: Record<string, number>;
  onChange: (quantities: Record<string, number>) => void;
};

export const ServiceAddOnsSelector = ({ addOns, quantities, onChange }: Props) => {
  const { t, lang, fmt } = useT();
  if (!addOns || addOns.length === 0) return null;
  const nameOf = (addOn: ServiceAddOn) => labelOf({ en: addOn.label_en, es: addOn.label_es }, addOn.id, lang);

  const setQuantity = (addOn: ServiceAddOn, raw: number) => {
    onChange({ ...quantities, [addOn.id]: clampAddOnQuantity(addOn, raw) });
  };

  return (
    <div className="mt-3">
      <div className="cap" style={{ marginBottom: 8 }}>{t('booking.addOns')}</div>
      <div className="space-y-2">
        {addOns.map((addOn) => {
          const qty = quantities[addOn.id] ?? (addOn.required ? 1 : 0);
          const min = addOn.required ? 1 : 0;
          return (
            <div
              key={addOn.id}
              className="flex items-center justify-between rounded-lg px-3 py-2"
              style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
              data-testid={`addon-selector-${addOn.id}`}
            >
              <div>
                <div className="text-xs font-medium" style={{ color: 'var(--ink-900)' }}>
                  {nameOf(addOn)}
                  {addOn.required && <span style={{ color: 'var(--ink-400)' }}> · {t('booking.addOnRequired')}</span>}
                </div>
                {/* Reference only — read verbatim from the add-on, never multiplied client-side. */}
                <div className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
                  {t('booking.price.perUnit', { price: fmt.money(addOn.price, addOn.currency || 'USD') })}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={t('booking.qty.decrease', { item: nameOf(addOn) })}
                  disabled={qty <= min}
                  onClick={() => setQuantity(addOn, qty - 1)}
                  className="rounded-full flex items-center justify-center"
                  style={{ width: 24, height: 24, border: '1px solid var(--line)', background: '#fff', opacity: qty <= min ? 0.4 : 1 }}
                >
                  <Minus size={12} />
                </button>
                <span className="text-sm w-6 text-center" style={{ color: 'var(--ink-900)' }}>{qty}</span>
                <button
                  type="button"
                  aria-label={t('booking.qty.increase', { item: nameOf(addOn) })}
                  disabled={qty >= addOn.max_qty}
                  onClick={() => setQuantity(addOn, qty + 1)}
                  className="rounded-full flex items-center justify-center"
                  style={{ width: 24, height: 24, border: '1px solid var(--line)', background: '#fff', opacity: qty >= addOn.max_qty ? 0.4 : 1 }}
                >
                  <Plus size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] mt-2" style={{ color: 'var(--ink-400)' }}>
        {t('booking.totalConfirmedByServer')}
      </p>
    </div>
  );
};
